import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Shield, Heart, Users, Ban, AlertTriangle,
  MessageSquare, Camera, DollarSign, Scale, CheckCircle
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0c49a145-2431-460e-8df4-1bea13020ea4/artifacts/j4k88xee_file_00000000c9b871f8a2879288a4f57131.png";

export default function RulesPage() {
  const navigate = useNavigate();

  const rules = [
    {
      icon: Heart,
      title: 'Respect & Dignity',
      color: 'text-pink-500',
      rules: [
        'Treat everyone with respect',
        'Do not use offensive or humiliating language',
        'No discrimination based on race, religion, gender, or sexual orientation',
        'Respect different opinions'
      ]
    },
    {
      icon: Ban,
      title: 'Prohibited Content',
      color: 'text-red-500',
      rules: [
        'Do not share sexual or pornographic content',
        'Do not share graphic violence or scary content',
        'No promotion of illegal drugs or illegal activities',
        'Do not share personal information of others without permission'
      ]
    },
    {
      icon: Users,
      title: 'Interactions with Others',
      color: 'text-blue-500',
      rules: [
        'Do not harass or intimidate other users',
        'Do not send unwanted messages (spam)',
        'Do not create fake accounts or impersonate others',
        'Report behavior that violates the rules'
      ]
    },
    {
      icon: Camera,
      title: 'Content & Media',
      color: 'text-purple-500',
      rules: [
        'Only share content you have the right to share',
        'Give credit when sharing others work',
        'Do not share false information or fake news',
        'Use filters and effects responsibly'
      ]
    },
    {
      icon: DollarSign,
      title: 'Monetization & Commerce',
      color: 'text-green-500',
      rules: [
        'Disclose all sponsored content',
        'Do not make false promises to get money',
        'Respect platform monetization agreements',
        'Do not commit fraud or deceive buyers on the Marketplace'
      ]
    },
    {
      icon: Shield,
      title: 'Security & Privacy',
      color: 'text-cyan-500',
      rules: [
        'Protect your login information',
        'Do not share your password with anyone',
        'Report suspicious activity or hacking',
        'Respect other users privacy'
      ]
    }
  ];

  const consequences = [
    { level: 'Warning', description: 'For first minor violation', color: 'bg-yellow-500/20 text-yellow-500' },
    { level: 'Temporary Suspension', description: 'For repeated or serious violations', color: 'bg-orange-500/20 text-orange-500' },
    { level: 'Permanent Suspension', description: 'For very serious or repeated violations', color: 'bg-red-500/20 text-red-500' }
  ];

  return (
    <div className="max-w-2xl mx-auto pb-20" data-testid="rules-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-bold text-lg">Rules & Principles</h1>
        </div>
      </header>

      <div className="p-4 space-y-8">
        {/* Hero */}
        <div className="text-center py-6">
          <img src={LOGO_URL} alt="FityH" className="w-20 h-20 mx-auto mb-4" />
          <h2 className="text-2xl font-bold">FityH Community Rules</h2>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">
            To create a safe and enjoyable space for everyone, we ask all users to respect these rules.
          </p>
        </div>

        {/* Mission */}
        <div className="bg-primary/10 rounded-2xl p-6 border border-primary/20">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Scale className="w-5 h-5 text-primary" />
            Our Mission
          </h3>
          <p className="mt-2 text-muted-foreground">
            FityH is a social platform to connect the Caribbean community. We want to create a space where everyone can express themselves, share experiences, and build positive connections.
          </p>
        </div>

        {/* Rules Sections */}
        {rules.map((section, index) => (
          <div key={index} className="space-y-3">
            <h3 className={`font-bold text-lg flex items-center gap-2 ${section.color}`}>
              <section.icon className="w-5 h-5" />
              {section.title}
            </h3>
            <div className="bg-secondary/20 rounded-2xl p-4 space-y-3">
              {section.rules.map((rule, rIndex) => (
                <div key={rIndex} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <p>{rule}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Consequences */}
        <div className="space-y-3">
          <h3 className="font-bold text-lg flex items-center gap-2 text-orange-500">
            <AlertTriangle className="w-5 h-5" />
            Violation Consequences
          </h3>
          <div className="space-y-2">
            {consequences.map((item, index) => (
              <div key={index} className={`${item.color} rounded-2xl p-4`}>
                <p className="font-semibold">{item.level}</p>
                <p className="text-sm opacity-80">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Report */}
        <div className="bg-secondary/30 rounded-2xl p-6">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Report Violations
          </h3>
          <p className="mt-2 text-muted-foreground">
            If you see content or behavior that violates these rules, please report it:
          </p>
          <ul className="mt-3 space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary" />
              Use the "Report" button on the post or profile
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary" />
              Contact support: <a href="mailto:fityhapp@gmail.com" className="text-primary">fityhapp@gmail.com</a>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary" />
              Go to the Support page in the app
            </li>
          </ul>
        </div>

        {/* Agreement */}
        <div className="text-center py-6 border-t border-border/50">
          <p className="text-sm text-muted-foreground">
            By using FityH, you agree to these rules.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Last updated: February 2026
          </p>
        </div>
      </div>
    </div>
  );
}
